<template>
  <div class="all-menu-setting">
    <PageHeaderLayout>
      <div class="menu-management">
        <div class="page-header">
          <h3 class="page-title">菜单管理</h3>
          <el-button type="primary" size="medium" icon="el-icon-plus" @click="handleAdd()" v-if="userPermissions.includes('menu_intact__create')">新增菜单</el-button>
        </div>

        <div class="search-bar">
          <el-input
            v-model="searchForm.name"
            placeholder="菜单名称"
            clearable
            prefix-icon="el-icon-search"
            style="width: 220px"
            @input="handleSearch"
          />
          <el-select
            v-model="searchForm.status"
            placeholder="状态"
            clearable
            class="w-120" style="margin-left: 12px"
            @change="handleSearch"
          >
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
          <el-button icon="el-icon-refresh" style="margin-left: 12px" @click="resetSearch">重置</el-button>
          <el-button icon="el-icon-s-unfold" @click="expandAll">展开全部</el-button>
          <el-button icon="el-icon-s-fold" @click="collapseAll">折叠全部</el-button>
        </div>

        <el-table
          ref="menuTable"
          :data="filteredMenuList"
          :key="tableKey"
          row-key="id"
          border
          stripe
          :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
          :default-expand-all="defaultExpandAll"
          v-loading="loading"
          class="menu-table"
        >
          <el-table-column prop="display_name" label="菜单名称" min-width="220">
            <template slot-scope="scope">
              <span :style="{ paddingLeft: (scope.row._depth || 0) * 20 + 'px' }">
                <svg-icon
                  v-if="scope.row.icon"
                  :icon-class="scope.row.icon"
                  style="margin-right: 6px; font-size: 15px; vertical-align: middle"
                />
                <i v-else class="el-icon-menu" style="margin-right:6px;color:#909399" />
                {{ scope.row.display_name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="icon" label="图标" width="100" align="center">
            <template slot-scope="scope">
              <svg-icon v-if="scope.row.icon" :icon-class="scope.row.icon" style="font-size:16px" />
              <span v-else style="color:#C0C4CC">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="permission_name" label="权限标识" min-width="170" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-tag v-if="scope.row.permission_name" size="small" type="info">{{ scope.row.permission_name }}</el-tag>
              <span v-else style="color:#C0C4CC">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="url" label="路由地址" min-width="180" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="scope.row.url">{{ scope.row.url }}</span>
              <span v-else style="color:#C0C4CC">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="sort" label="排序" width="70" align="center" />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small" effect="plain">
                {{ scope.row.status === 1 ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" icon="el-icon-plus" @click="handleAdd(scope.row)" v-if="userPermissions.includes('menu_intact__create')">新增</el-button>
              <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(scope.row)" v-if="userPermissions.includes('menu_intact__update')">编辑</el-button>
              <el-button type="text" size="small" icon="el-icon-delete" style="color:#F56C6C" @click="handleDelete(scope.row)" v-if="userPermissions.includes('menu_intact__delete')">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="table-footer">
          共 <b>{{ flatCount }}</b> 条记录
        </div>
      </div>
    </PageHeaderLayout>

    <!-- Add / Edit Dialog -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <el-form ref="menuForm" :model="formData" :rules="formRules" label-width="90px" label-position="right">
        <el-form-item label="菜单类型" prop="type">
          <el-radio-group v-model="formData.type" @change="onTypeChange">
            <el-radio label="menu">菜单</el-radio>
            <el-radio label="button">按钮</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="上级菜单" prop="parent_id">
          <el-select v-model="formData.parent_id" placeholder="请选择上级菜单（留空为顶级）" clearable filterable class="w-full">
            <el-option
              v-for="item in parentOptions"
              :key="item.id"
              :label="item.label"
              :value="item.id"
              :disabled="item.disabled"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="菜单名称" prop="display_name">
          <el-input v-model="formData.display_name" placeholder="请输入菜单名称" maxlength="20" show-word-limit />
        </el-form-item>
        <el-form-item label="图标" prop="icon">
          <el-input v-model="formData.icon" placeholder="请选择图标" readonly>
            <el-button slot="append" icon="el-icon-more-outline" @click="openIconPicker">选择</el-button>
          </el-input>
        </el-form-item>
        <el-form-item label="路由地址" prop="url">
          <el-input v-model="formData.url" placeholder="请输入路由地址，如 /system/menu" />
        </el-form-item>
        <el-form-item label="权限标识" prop="permission_name">
          <el-input v-model="formData.permission_name" placeholder="请输入权限标识" />
        </el-form-item>
        <el-form-item label="排序号" prop="sort">
          <el-input-number v-model="formData.sort" :min="0" :max="999" class="w-120" controls-position="right" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 图标选择弹窗 -->
    <el-dialog
      title="选择图标"
      :visible.sync="iconPickerVisible"
      width="720px"
      :close-on-click-modal="false"
    >
      <div class="icon-picker-toolbar">
        <el-input
          v-model="iconSearchKey"
          placeholder="搜索图标名称..."
          prefix-icon="el-icon-search"
          clearable
          class="icon-picker-search"
        />
      </div>
      <div v-if="filteredIconList.length" class="icon-picker-grid">
        <div
          v-for="name in filteredIconList"
          :key="name"
          class="icon-picker-item"
          :class="{ 'is-selected': name === tempIcon }"
          @click="tempIcon = name"
        >
          <div class="icon-picker-preview">
            <svg-icon :icon-class="name" class="icon-picker-img" />
          </div>
          <span class="icon-picker-name">{{ name }}</span>
        </div>
      </div>
      <div v-else class="icon-picker-empty">暂无匹配图标</div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="iconPickerVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmIcon">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeaderLayout from '@/layouts/PageHeaderLayout'
import { mapGetters } from 'vuex'
import menuApi from '@/api/menu_setting'

function assignDepth(tree, depth) {
  depth = depth || 0
  for (const item of tree) {
    item._depth = depth
    if (item.children && item.children.length) {
      assignDepth(item.children, depth + 1)
    }
  }
}

function flattenTree(tree, result) {
  result = result || []
  for (const item of tree) {
    const { children, ...rest } = item
    result.push(rest)
    if (children && children.length) {
      flattenTree(children, result)
    }
  }
  return result
}

export default {
  name: 'AllMenuSetting',
  components: { PageHeaderLayout },
  data() {
    return {
      loading: false,
      tableKey: 0,
      defaultExpandAll: true,
      menuTree: [],
      filteredMenuList: [],
      searchForm: { name: '', status: '' },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      editId: null,
      submitting: false,
      iconList: [],
      iconPickerVisible: false,
      iconSearchKey: '',
      tempIcon: '',
      formData: {
        type: 'menu',
        parent_id: null,
        display_name: '',
        icon: '',
        url: '',
        permission_name: '',
        sort: 0,
        status: 1
      },
      formRules: {
        display_name: [
          { required: true, message: '请输入菜单名称', trigger: 'blur' }
        ],
        permission_name: [
          { required: true, message: '请输入权限标识', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['userPermissions']),
    parentOptions() {
      const flat = flattenTree(this.menuTree).filter(
        item => item.type === 'menu' && item.id !== this.editId
      )
      return flat.map(item => ({
        id: item.id,
        label: '\u2502  '.repeat(item._depth || 0) + item.display_name,
        disabled: false
      }))
    },
    flatCount() {
      return flattenTree(this.filteredMenuList).length
    },
    filteredIconList() {
      if (!this.iconSearchKey) return this.iconList
      const kw = this.iconSearchKey.toLowerCase()
      return this.iconList.filter(name => name.toLowerCase().includes(kw))
    }
  },
  methods: {
    async fetchMenuTree() {
      this.loading = true
      try {
        const res = await menuApi.getMenuTree()
        let tree = []
        if (Array.isArray(res)) {
          tree = res
        } else if (res && typeof res === 'object') {
          tree = Object.values(res)
        }
        assignDepth(tree)
        this.menuTree = tree
        this.filteredMenuList = this.menuTree
      } catch (e) {
        this.$message.error('获取菜单列表失败')
      } finally {
        this.loading = false
      }
    },

    filterTree(tree, name, status) {
      const result = []
      for (const item of tree) {
        let childMatches = []
        if (item.children && item.children.length) {
          childMatches = this.filterTree(item.children, name, status)
        }
        const nameMatch = !name || (item.display_name && item.display_name.indexOf(name) !== -1)
        const statusMatch = status === '' || status == null || item.status === status
        if ((nameMatch && statusMatch) || childMatches.length > 0) {
          result.push({
            ...item,
            children: childMatches.length > 0 ? childMatches : item.children
          })
        }
      }
      return result
    },

    handleSearch() {
      const name = this.searchForm.name.trim()
      const status = this.searchForm.status
      this.filteredMenuList = this.filterTree(this.menuTree, name, status)
    },

    resetSearch() {
      this.searchForm = { name: '', status: '' }
      this.filteredMenuList = this.menuTree
    },

    expandAll() {
      this.defaultExpandAll = true
      this.tableKey++
    },

    collapseAll() {
      this.defaultExpandAll = false
      this.tableKey++
    },

    onTypeChange(val) {
      // 切换类型时保留所有已填字段
    },

    loadIconList() {
      try {
        const ctx = require.context('@/assets/icons/svg/', false, /\.svg$/)
        this.iconList = ctx.keys().map(key => key.replace('./', '').replace('.svg', ''))
      } catch (e) {
        this.iconList = []
      }
    },

    openIconPicker() {
      this.tempIcon = this.formData.icon || ''
      this.iconSearchKey = ''
      this.iconPickerVisible = true
    },

    confirmIcon() {
      this.formData.icon = this.tempIcon
      this.iconPickerVisible = false
    },

    handleAdd(parent) {
      this.isEdit = false
      this.editId = null
      this.dialogTitle = parent ? '新增子菜单' : '新增菜单'
      this.formData = {
        type: 'menu',
        parent_id: parent ? parent.id : null,
        display_name: '',
        icon: '',
        url: '',
        permission_name: '',
        sort: 0,
        status: 1
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },

    handleEdit(row) {
      this.isEdit = true
      this.editId = row.id
      this.dialogTitle = '编辑菜单'
      this.formData = {
        type: row.type || 'menu',
        parent_id: row.parent_id != null ? row.parent_id : null,
        display_name: row.display_name || '',
        icon: row.icon || '',
        url: row.url || '',
        permission_name: row.permission_name || '',
        sort: row.sort != null ? row.sort : 0,
        status: row.status != null ? row.status : 1
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },

    async handleDelete(row) {
      if (row.children && row.children.length) {
        this.$message.warning('该菜单存在子菜单，请先删除子菜单')
        return
      }
      try {
        await this.$confirm(
          '确定要删除菜单 "' + row.display_name + '" 吗？此操作不可恢复。',
          '确认删除',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )
        await menuApi.deleteMenu(row.id)
        this.$message.success('删除成功')
        await this.fetchMenuTree()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    async submitForm() {
      const valid = await this.$refs.menuForm.validate().catch(() => false)
      if (!valid) return
      this.submitting = true
      try {
        const payload = {
          parent_id: this.formData.parent_id != null ? String(this.formData.parent_id) : '0',
          type: this.formData.type,
          display_name: this.formData.display_name,
          icon: this.formData.icon,
          url: this.formData.url,
          permission_name: this.formData.permission_name,
          sort: String(this.formData.sort),
          status: String(this.formData.status)
        }
        if (this.isEdit) {
          payload.id = this.editId
          await menuApi.updateMenu(payload)
          this.$message.success('编辑成功')
        } else {
          await menuApi.createMenu(payload)
          this.$message.success('新增成功')
        }
        this.dialogVisible = false
        await this.fetchMenuTree()
      } catch (e) {
        this.$message.error(this.isEdit ? '编辑失败' : '新增失败')
      } finally {
        this.submitting = false
      }
    }
  },
  mounted() {
    this.fetchMenuTree()
    this.loadIconList()
  }
}
</script>

<style lang="scss" scoped>
.all-menu-setting {
  height: 100%;
  background: #f5f7fa;
}

.menu-management {
  padding: 20px;
  background: #fff;
  border-radius: 4px;
  min-height: calc(100vh - 140px);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.search-bar {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.menu-table {
  margin-bottom: 12px;
}

.table-footer {
  text-align: right;
  color: #909399;
  font-size: 13px;
}

.dialog-footer {
  text-align: right;
}

/* 图标选择器 */
.icon-picker-toolbar {
  margin-bottom: 16px;
}

.icon-picker-search {
  width: 100%;
}

.icon-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  max-height: 360px;
  overflow-y: auto;
}

.icon-picker-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 6px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.icon-picker-item:hover {
  border-color: #1890ff;
}

.icon-picker-item.is-selected {
  border-color: #1890ff;
  background: #e6f7ff;
}

.icon-picker-preview {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}

.icon-picker-img {
  width: 28px;
  height: 28px;
}

.icon-picker-name {
  font-size: 11px;
  color: #606266;
  text-align: center;
  word-break: break-all;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-width: 100%;
}

.icon-picker-empty {
  text-align: center;
  padding: 40px;
  color: #c0c4cc;
  font-size: 14px;
}
</style>
